import type { WebinarContentKind, WebinarContentPiece } from "@xop/dashboard-shared";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageIcon from "@mui/icons-material/Image";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  Chip,
  Collapse,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { toast } from "../../../../components/ui/use-toast.js";
import { downloadBlob, webinarsApi } from "../client.js";
import {
  BlogPreview,
  LinkedInPreview,
  previewLine,
  previewStageSx,
  stripEmphasis,
} from "./ContentPreview.js";
import { StepFooter, StepIntro, type StepProps } from "../stepShared.js";

/**
 * Step 3 — turn the recording into a blog post and a LinkedIn post.
 *
 * Claude writes the words from the transcript the operator pastes in;
 * gpt-image-1 draws XOP-branded hero art to go with them. Every piece is
 * exportable as Markdown, standalone HTML, plain text, or the PNG, because
 * the point is to publish it on the real blog and on LinkedIn — not to admire
 * it in this tab.
 *
 * The transcript box is the honest bottleneck and the UI says so: without one
 * the AI can only work from the title and description, and the result is
 * generic. Better to tell the operator that than to quietly produce filler.
 */
export function StepContent({ detail, refresh, canEdit, markDone }: StepProps): JSX.Element {
  const { webinar, content } = detail;
  const [transcript, setTranscript] = useState(webinar.transcript);
  const [recording, setRecording] = useState(webinar.recording_url);
  const [hint, setHint] = useState("");
  // Once something has been written, the transcript box stops being the point
  // of the screen — the generated posts are. It folds away, and the operator
  // opens it again only to re-source a regeneration.
  const [sourceOpen, setSourceOpen] = useState(content.length === 0);

  const saveSource = useMutation({
    mutationFn: () =>
      webinarsApi.update(webinar.id, {
        transcript,
        recording_url: recording,
      }),
    onSuccess: () => {
      refresh();
      toast({ title: "Saved" });
    },
    onError: (e: Error) =>
      toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  const generate = useMutation({
    mutationFn: (kind: WebinarContentKind) =>
      webinarsApi.generateContent(webinar.id, {
        kind,
        ...(hint.trim() ? { hint: hint.trim() } : {}),
        with_image: true,
      }),
    onSuccess: (res) => {
      refresh();
      toast({
        title: res.content.kind === "blog" ? "Blog post written" : "LinkedIn post written",
        ...(res.image_available
          ? {}
          : { description: "The picture could not be generated this time, but the words are saved." }),
      });
    },
    onError: (e: Error) =>
      toast({
        title: "The AI could not write this",
        description: e.message,
        variant: "destructive",
      }),
  });

  const sourceDirty =
    transcript !== webinar.transcript || recording !== webinar.recording_url;
  const busy = generate.isPending;

  const hasContent = content.length > 0;

  const sourcePanel = (
    <Stack spacing={2}>
      <TextField
        label="Link to the recording (optional)"
        placeholder="https://..."
        value={recording}
        onChange={(e) => setRecording(e.target.value)}
        disabled={!canEdit}
        fullWidth
        size="small"
        helperText="Only for your own reference, and to include in the thank-you email."
      />
      <TextField
        label="What was said (transcript or your notes)"
        placeholder="Paste the transcript here. The more detail, the better the writing."
        value={transcript}
        onChange={(e) => setTranscript(e.target.value)}
        disabled={!canEdit}
        multiline
        minRows={hasContent ? 4 : 6}
        fullWidth
        inputProps={{ "data-testid": "webinar-transcript" }}
      />
      {sourceDirty && canEdit && (
        <Box>
          <Button
            variant="outlined"
            onClick={() => saveSource.mutate()}
            disabled={saveSource.isPending}
            data-testid="save-transcript"
          >
            {saveSource.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>
      )}
      {!webinar.transcript.trim() && (
        <Alert severity="warning">
          Without a transcript the AI can only work from the title and description, so the
          writing will be general. Paste the transcript first if you have one.
        </Alert>
      )}
    </Stack>
  );

  const generateRow = canEdit ? (
    <Stack spacing={1.5}>
      <TextField
        label="Anything to emphasise? (optional)"
        placeholder="e.g. lean on the 911 resilience angle"
        value={hint}
        onChange={(e) => setHint(e.target.value)}
        fullWidth
        size="small"
      />
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <Button
          variant={hasContent ? "outlined" : "contained"}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArticleIcon />}
          onClick={() => generate.mutate("blog")}
          disabled={busy || sourceDirty}
          data-testid="generate-blog"
        >
          {hasContent ? "Another blog post" : "Write the blog post"}
        </Button>
        <Button
          variant={hasContent ? "outlined" : "contained"}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LinkedInIcon />}
          onClick={() => generate.mutate("linkedin")}
          disabled={busy || sourceDirty}
          data-testid="generate-linkedin"
        >
          {hasContent ? "Another LinkedIn post" : "Write the LinkedIn post"}
        </Button>
        {sourceDirty && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Save your changes first.
          </Typography>
        )}
        {busy && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            This takes about a minute.
          </Typography>
        )}
      </Box>
    </Stack>
  ) : null;

  return (
    <Box>
      <StepIntro>
        Paste what was said in the webinar, then let the AI write a blog post and a LinkedIn post
        from it. Claude writes the words, ChatGPT draws the picture.
      </StepIntro>

      {/* Once there is something to read, it comes FIRST. Burying finished
          posts under the transcript box meant scrolling past a screenful of
          inputs to see what the AI actually produced. */}
      {hasContent && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {content.map((piece) => (
            <ContentCard
              key={piece.id}
              webinarId={webinar.id}
              piece={piece}
              canEdit={canEdit}
              onDeleted={refresh}
            />
          ))}
        </Stack>
      )}

      {hasContent ? (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden" }}>
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={sourceOpen}
            onClick={() => setSourceOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSourceOpen((v) => !v);
              }
            }}
            data-testid="toggle-source"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1.5,
              cursor: "pointer",
              "&:hover": { bgcolor: "surfaceSoft" },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
              Write another, or change the source material
            </Typography>
            <ExpandMoreIcon
              sx={{
                color: "text.secondary",
                transition: (t) => t.transitions.create("transform", { duration: 180 }),
                transform: sourceOpen ? "rotate(180deg)" : "none",
              }}
            />
          </Box>
          <Collapse in={sourceOpen} unmountOnExit>
            <Box sx={{ px: 2, pb: 2.5, pt: 0.5, borderTop: 1, borderColor: "divider" }}>
              <Stack spacing={2.5} sx={{ pt: 2 }}>
                {sourcePanel}
                {generateRow}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      ) : (
        <Stack spacing={3}>
          {sourcePanel}
          {generateRow}
        </Stack>
      )}

      <StepFooter
        step="content"
        detail={detail}
        markDone={markDone}
        canEdit={canEdit}
        {...(content.length === 0
          ? { disabledReason: "Write at least one post first." }
          : {})}
      />
    </Box>
  );
}

function ContentCard({
  webinarId,
  piece,
  canEdit,
  onDeleted,
}: {
  webinarId: number;
  piece: WebinarContentPiece;
  canEdit: boolean;
  onDeleted: () => void;
}): JSX.Element {
  // Collapsed by default so a webinar with four generated pieces is still a
  // scannable list; expanding gives the full artifact.
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: () => webinarsApi.removeContent(webinarId, piece.id),
    onSuccess: onDeleted,
  });

  // The hero art is a ~1 MB base64 PNG, deliberately stripped from the list
  // read. It loads WITH the expansion rather than behind its own button: the
  // operator is deciding whether this is publishable, and text without its
  // art answers a different question.
  const full = useQuery({
    queryKey: ["webinars", webinarId, "content", piece.id],
    queryFn: () => webinarsApi.getContent(webinarId, piece.id),
    enabled: open && piece.has_image,
    staleTime: 5 * 60_000,
  });
  const imageSrc = full.data?.image_b64 ? `data:image/png;base64,${full.data.image_b64}` : null;
  const imageLoading = open && piece.has_image && !imageSrc && !full.isError;

  const download = async (format: "md" | "html" | "txt" | "png"): Promise<void> => {
    try {
      const blob = await webinarsApi.exportContent(webinarId, piece.id, format);
      downloadBlob(blob, `${slug(piece.title)}-${piece.kind}.${format}`);
    } catch (e) {
      toast({
        title: "Download failed",
        description: (e as Error)?.message ?? "",
        variant: "destructive",
      });
    }
  };

  const isBlog = piece.kind === "blog";

  return (
    <Card variant="outlined" data-testid={`content-piece-${piece.id}`}>
      {/* Header doubles as the expand control — the whole row is the target,
          not a lone chevron. */}
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        data-testid={`toggle-piece-${piece.id}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          cursor: "pointer",
          "&:hover": { bgcolor: "surfaceSoft" },
          "&:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mb: 0.5, flexWrap: "wrap" }}>
            <Chip
              size="small"
              color="primary"
              icon={isBlog ? <ArticleIcon /> : <LinkedInIcon />}
              label={isBlog ? "Blog post" : "LinkedIn post"}
            />
            {piece.has_image && (
              <Chip size="small" variant="outlined" icon={<ImageIcon />} label="With picture" />
            )}
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.35 }}>
            {piece.title}
          </Typography>
          {!open && (
            <Typography
              sx={{
                fontSize: 13,
                color: "text.secondary",
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {previewLine(piece.body_markdown)}
            </Typography>
          )}
        </Box>

        {canEdit && (
          <IconButton
            size="small"
            aria-label="Delete this post"
            onClick={(e) => {
              e.stopPropagation();
              remove.mutate();
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
        <ExpandMoreIcon
          sx={{
            color: "text.secondary",
            transition: (t) => t.transitions.create("transform", { duration: 180 }),
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
          <Box sx={previewStageSx()}>
            {isBlog ? (
              <BlogPreview piece={piece} imageSrc={imageSrc} imageLoading={imageLoading} />
            ) : (
              <LinkedInPreview piece={piece} imageSrc={imageSrc} imageLoading={imageLoading} />
            )}
            {full.isError && (
              <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1.5 }}>
                The picture could not be loaded. The text below is unaffected.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.75,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <ButtonGroup size="small" variant="outlined">
                <Button startIcon={<DownloadIcon />} onClick={() => void download("md")}>
                  Markdown
                </Button>
                <Button onClick={() => void download("html")}>HTML</Button>
                <Button onClick={() => void download("txt")}>Plain text</Button>
                {piece.has_image && <Button onClick={() => void download("png")}>Picture</Button>}
              </ButtonGroup>
              <Button
                size="small"
                onClick={() => {
                  void navigator.clipboard?.writeText(
                    isBlog ? piece.body_markdown : stripEmphasis(piece.body_markdown),
                  );
                  toast({ title: isBlog ? "Markdown copied" : "Post copied" });
                }}
              >
                Copy the text
              </Button>
            </Box>

            {/* Provenance sits with the artifact, not in the list header —
                it is something you check while reading, not while scanning. */}
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {[
                piece.text_model && `Written by ${piece.text_model}`,
                piece.has_image && piece.image_model && `picture by ${piece.image_model}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
}

/** Lower-kebab filename stem. Mirrors the server's `exportFilename` so the
 * name in the download bar matches what the API would have chosen. */
function slug(title: string): string {
  return (
    title
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "webinar"
  );
}
